import type { IDateRangeState } from '~/presentation/ui';
import type { DateTimeFilter } from '../types';

import { InlineGrid, TextField, Icon, InlineStack } from '@shopify/polaris';
import { dayjsTz, convertDateToTz, dayjsTzToLocalTZ } from '~/utils/dayjs';
import { isValidDate, formatDate } from '~/presentation/ui';
import { useCallback, useEffect, useState } from 'react';
import { ArrowRightIcon } from '@shopify/polaris-icons';

interface DateTimeFilterInputsProps {
  onBlur?: () => void;
  dateRange: DateTimeFilter;
  setDateTimePicked: (value: IDateRangeState) => void;
}

export function DateTimeFilterInputs({
  onBlur,
  dateRange,
  setDateTimePicked,
}: DateTimeFilterInputsProps) {
  const [isFocus, setIsFocus] = useState(false);
  const [inputValues, setInputValues] = useState<{
    since?: string;
    until?: string;
  }>({});

  const formatDisplayDate = useCallback(
    (timestamp: string | number, isEnd = false) => {
      if (!dateRange.isVersion || (isEnd && dateRange.isCurrentVersion))
        return formatDate(timestamp, 'FULL');

      return formatDate(timestamp, 'FULL_WITH_TIME');
    },
    [dateRange.isVersion, dateRange.isCurrentVersion],
  );

  useEffect(() => {
    if (isFocus) return;
    setInputValues({
      since: formatDisplayDate(dateRange.since),
      until: formatDisplayDate(dateRange.until, true),
    });
  }, [dateRange, formatDisplayDate]);

  function handleStartInputValueChange(value: any) {
    setInputValues((prevState) => {
      return { ...prevState, since: value };
    });
    if (isValidDate(value)) {
      const newSinceDate = convertDateToTz(value).startOf('day');
      const untilDate = convertDateToTz(dateRange.until).endOf('day');
      const endDate = newSinceDate.isAfter(untilDate) ? newSinceDate.endOf('day') : untilDate;

      setDateTimePicked({
        start: dayjsTzToLocalTZ(newSinceDate).toDate(),
        end: dayjsTzToLocalTZ(endDate).toDate(),
      });
    }
  }
  function handleEndInputValueChange(value: any) {
    setInputValues((prevState) => ({ ...prevState, until: value }));
    if (isValidDate(value)) {
      const nowEndOfDay = dayjsTz().endOf('day');
      const newUntilDate = convertDateToTz(value).endOf('day');
      const clampedUntil = newUntilDate.isAfter(nowEndOfDay) ? nowEndOfDay : newUntilDate;
      const sinceDate = convertDateToTz(dateRange.since).startOf('day');
      const startDate = clampedUntil.isBefore(sinceDate) ? clampedUntil.startOf('day') : sinceDate;

      setDateTimePicked({
        start: dayjsTzToLocalTZ(startDate).toDate(),
        end: dayjsTzToLocalTZ(clampedUntil).toDate(),
      });
    }
  }

  function handleInputBlur() {
    setIsFocus(false);
    setInputValues({
      since: formatDisplayDate(dateRange.since),
      until: formatDisplayDate(dateRange.until, true),
    });
    onBlur && onBlur();
  }

  function handleFocusStartInput() {
    setIsFocus(true);
    setInputValues({
      ...inputValues,
      since: formatDate(dateRange.since, 'YMD'),
    });
  }

  function handleFocusEndInput() {
    setIsFocus(true);
    setInputValues({
      ...inputValues,
      until: formatDate(dateRange.until, 'YMD'),
    });
  }
  return (
    <InlineGrid gap="200" columns="1fr auto 1fr">
      <TextField
        role="combobox"
        label={'Since'}
        labelHidden
        // prefix={<Icon source={CalendarIcon} />}
        value={dateRange.since ? inputValues.since : 'MMMM D, YYYY'}
        onChange={handleStartInputValueChange}
        onBlur={() => handleInputBlur()}
        onFocus={() => handleFocusStartInput()}
        autoComplete="off"
      />
      <InlineStack>
        <Icon source={ArrowRightIcon} tone="subdued" />
      </InlineStack>
      <TextField
        role="combobox"
        label={'Until'}
        labelHidden
        // prefix={<Icon source={CalendarIcon} />}
        value={dateRange.until ? inputValues.until : 'MMMM D, YYYY'}
        onChange={handleEndInputValueChange}
        onBlur={() => handleInputBlur()}
        onFocus={() => handleFocusEndInput()}
        autoComplete="off"
      />
    </InlineGrid>
  );
}
